import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { PageHeader } from '@/app/layouts/PageHeader'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { EmptyState, ErrorState } from '@/components/ui/EmptyState'
import { LockBadge } from '@/components/ui/LockBadge'
import { PostListSkeleton } from '@/components/ui/Skeleton'
import { ListEditDialog, useList, useListTimeline } from '@/features/lists'
import { PostList } from '@/features/posts'
import { useI18n } from '@/i18n'
import { useFormat } from '@/lib/format'

export function ListPage() {
  const { t, tp } = useI18n()
  const navigate = useNavigate()
  const { compactCount } = useFormat()
  const { listId = '' } = useParams()
  const list = useList(listId)
  const timeline = useListTimeline(listId)
  const [editing, setEditing] = useState(false)

  if (list.isPending) {
    return (
      <>
        <PageHeader title={t('nav.lists')} back />
        <PostListSkeleton count={3} />
      </>
    )
  }
  if (list.isError) {
    const missing = list.error instanceof ApiError && list.error.status === 404
    return (
      <>
        <PageHeader title={t('nav.lists')} back />
        {missing ? (
          <EmptyState title={t('lists.notFoundTitle')} description={t('lists.notFoundBody')} />
        ) : (
          <ErrorState message={list.error.message} onRetry={() => list.refetch()} />
        )}
      </>
    )
  }

  const data = list.data
  const isOwner = data.viewer.is_owner
  return (
    <>
      <PageHeader title={data.name} subtitle={`@${data.owner.username}`} back />

      <section className="border-b border-line px-4 py-4">
        {/* 页面的 h1 是顶部粘性标题栏，这里降一级 */}
        <h2 className="flex items-center gap-1 text-xl font-extrabold">
          <span className="min-w-0 break-words">{data.name}</span>
          {data.is_private && <LockBadge className="size-4" />}
        </h2>
        {data.description && <p className="mt-1 text-[15px]">{data.description}</p>}
        <Link
          to={`/${data.owner.username}`}
          className="mt-2 inline-flex items-center gap-2 text-[15px] hover:underline"
        >
          <Avatar src={data.owner.avatar_url} alt={data.owner.display_name} size="xs" />
          <span className="font-bold">{data.owner.display_name}</span>
          <span className="font-mono text-[14px] text-ink-muted">@{data.owner.username}</span>
        </Link>
        <div className="mt-3 flex items-center gap-4">
          <Link to={`/lists/${data.id}/members`} className="text-[15px] hover:underline">
            {tp('lists.memberCount', data.member_count, { count: compactCount(data.member_count) })}
          </Link>
          {isOwner && (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              {t('lists.edit')}
            </Button>
          )}
        </div>
      </section>

      <PostList
        query={timeline}
        emptyTitle={t('lists.timelineEmptyTitle')}
        emptyDescription={
          isOwner && data.member_count === 0
            ? t('lists.timelineEmptyOwner')
            : t('lists.timelineEmptyBody')
        }
      />

      {editing && (
        <ListEditDialog
          open
          list={data}
          onClose={() => setEditing(false)}
          onDeleted={() => navigate('/lists', { replace: true })}
        />
      )}
    </>
  )
}
